import axios from 'axios';
import {
	PageLayout,
	Card,
	CardBody,
	Button,
	CardPlaceholder,
	Permissions,
	getRegistrationUrl,
	getAdminUrl,
	getTrainingUrl,
	Icon,
	Icons,
	useAuthorisationContext,
	useLangContext,
	StringHelpers,
	Variants,
	checkTheme,
	Themes,
	route,
	API_ENDPOINTS,
	customToast,
	replaceKeyWithValue,
	Roles,
	getThemeFromUrl,
} from 'carrier-fe';
import { useState, useEffect } from 'react';
import PromoCarousel from '../../../components/PromoCarousel';

function Dashboard() {
	const { getLangPage } = useLangContext();
	const { hasPermissionAccess, hasRoleAccess } = useAuthorisationContext();
	const [loyaltyLink, setLoyaltyLink] = useState<string | undefined>(undefined);
	const [rewardPoints, setRewardPoints] = useState<number>(0);
	const [lang, setLang] = useState<any>(null);

	const hasRewardsPermission = 
		hasPermissionAccess(Permissions.VIESSMANN_REWARDS_ACCESS) || 
		hasPermissionAccess(Permissions.TOSHIBA_REWARDS_ACCESS);
	const hasRewardsTheme = 
		checkTheme(Themes.Viessmann) || 
		checkTheme(Themes.Toshiba);
	const canAccessRewards = 
		hasRoleAccess(Roles.COMPANY_MANAGER) &&
		hasRewardsPermission && 
		hasRewardsTheme;

	useEffect(() => {
		if (lang === null) {
			load();
		}
		if (canAccessRewards) {
			fetchRewardsInfo();
			fetchRewardsLink();
		}
	}, []);

	const load = async () => {
		getLangPage('dashboard/dashboard')
			.then((res: any) => {
				if (typeof res === 'undefined' || res === null) {
					setLang([]);
				} else {
					setLang(res);

					document.title = StringHelpers.title(
						res?.page.title ?? 'Dashboard'
					);
				}
			})
			.catch((err: any) => console.error(err));
	};

	const getTileById = (id: string) => {
		if (!lang || !lang.page || !lang.page.tiles) return null;

		for (let i = 0; i < lang.page.tiles.length; i++) {
			if (lang.page.tiles[i]['id'] === id) {
				return lang.page.tiles[i];
			}
		}

		return null;
	};

	const fetchRewardsInfo = async () => {
		try {
			const response = await axios.get<{ data: { points: any } }>(
				route(API_ENDPOINTS.GENERIC.REWARDS.INFO)
			);
			setRewardPoints(response.data.data.points);
		} catch (error: any) {
			customToast({
				title: error.message || "Error loading rewards info",
				variant: Variants.Danger
			});
		}
	};

	const fetchRewardsLink = async () => {
		try {
			const response = await axios.get<{ data: { link: string } }>(
				route(API_ENDPOINTS.GENERIC.REWARDS.LINK)
			);
			setLoyaltyLink(response?.data?.data?.link);
		} catch (error: any) {
			customToast({
				title: error.message || "Error fetching rewards link",
				variant: Variants.Danger
			});
		} 
	};

	const renderRegistrationTile = () => {
		if (
			!hasPermissionAccess(
				Permissions.REGISTRATION_AND_WARRANTY_PLATFORM_VIEW
			)
		)
			return null;

		let content = getTileById('registration.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.PROFILE}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title
							: 'Registrations'}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description && !!content.description[getThemeFromUrl()]
							? content.description[getThemeFromUrl()]
							: 'Access the registrations and warranty dashboard'}
					</p>
					<Button
						label={
							!!content && !!content.button && !!content.button[getThemeFromUrl()]
								? content.button[getThemeFromUrl()]
								: 'Go to'
						}
						href={getRegistrationUrl() + '/dashboard'}
						className="d-inline-flex"
						variant={Variants.Info}
						disabled={!(checkTheme(Themes.Viessmann) || checkTheme(Themes.Toshiba))}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderTrainingTile = () => {
		if (!hasPermissionAccess(Permissions.TRAINING_PLATFORM_VIEW))
			return null;

		let content = getTileById('training.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.APARTMENT}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title
							: 'Training'}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description
							? content.description
							: 'Access the training dashboard'}
					</p>
					<Button
						label={
							!!content && !!content.button && !!content.button[getThemeFromUrl()]
								? content.button[getThemeFromUrl()]
								: 'Go to'
						}
						href={getTrainingUrl() + '/dashboard'}
						className="d-inline-flex"
						variant={Variants.Info}
						disabled={
							checkTheme(Themes.Viessmann) 
								|| checkTheme(Themes.Toshiba) 
							? false 
							: true
						}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderAdminTile = () => {
		if (!hasPermissionAccess(Permissions.ADMIN_PLATFORM_VIEW))
			return null;

		let content = getTileById('admin.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.DASHBOARD}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title ? content.title : 'Admin'}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description
							? content.description
							: 'Access the admin dashboard'}
					</p>
					<Button
						label={
							!!content && !!content.button
								? content.button
								: 'Go to'
						}
						href={getAdminUrl() + '/dashboard'}
						className="d-inline-flex"
						variant={Variants.Info}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderVPlusTile = () => {
		if (
			!hasPermissionAccess(Permissions.VIESSMANN_REWARDS_ACCESS) ||
			!hasRoleAccess(Roles.COMPANY_MANAGER) ||
			!checkTheme(Themes.Viessmann)
		)
			return null;

		let content = getTileById('rewards.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.DASHBOARD}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title.viessmann
							: 'VPlus Rewards'}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description.viessmann
							? replaceKeyWithValue(
								content.description.viessmann,
								'points',
								String(rewardPoints)
							) 
							: 'View and spend my reward points.'}
					</p>
					<Button
						label={
							!!content && !!content.button
								? content.button
								: 'Go to'
						}
						href={loyaltyLink}
						disabled={
							loyaltyLink ? false : true
						}
						className="d-inline-flex"
						variant={Variants.Info}
						externalLink
						onClick={
							loyaltyLink ?
							undefined :
							() => customToast({
								title: 'Error loading loyalty link',
								variant: Variants.Danger,
							})
						}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderToshibaRewardsTile = () => {
		if (
			!hasPermissionAccess(Permissions.TOSHIBA_REWARDS_ACCESS) ||
			!hasRoleAccess(Roles.COMPANY_MANAGER) ||
			!checkTheme(Themes.Toshiba)
		)
			return null;

		let content = getTileById('rewards.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.DASHBOARD}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title.toshiba
							: 'Toshiba Rewards'}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description.toshiba
							? replaceKeyWithValue(
								content.description.toshiba,
								'points',
								String(rewardPoints)
							) 
							: 'View and spend my reward points.'}
					</p>
					<Button
						label={
							!!content && !!content.button
								? content.button
								: 'Go to'
						}
						href={loyaltyLink}
						disabled={
							loyaltyLink ? false : true
						}
						className="d-inline-flex"
						variant={Variants.Info}
						externalLink
						onClick={
							loyaltyLink ?
							undefined :
							() => customToast({
								title: 'Error loading loyalty link',
								variant: Variants.Danger,
							})
						}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderSupportTile = () => {
		if (
			!hasPermissionAccess(Permissions.SUPPORT_PLATFORM_VIEW)
			|| (!checkTheme(Themes.Viessmann) && !checkTheme(Themes.Toshiba))
			|| (checkTheme(Themes.Toshiba) 
				&& !hasRoleAccess(Roles.PLATFORM_MANAGER) 
				&& !hasRoleAccess(Roles.SUPER_ADMIN)
				&& !hasRoleAccess(Roles.COMPANY_MANAGER)
			)
		)
			return null;

		let content = getTileById('support.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.DASHBOARD}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title && !!content.title[getThemeFromUrl()] 
							? content.title[getThemeFromUrl()] 
							: 'Support'}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description && !!content.description[getThemeFromUrl()]
							? content.description[getThemeFromUrl()]
							: 'Order brochures, download images & book a call-out.'
						}
					</p>
					<Button
						label={
							!!content && !!content.button
								? content.button
								: 'Go to'
						}
						href={'/support'}
						className="d-inline-flex"
						variant={Variants.Info}
					/>
				</CardBody>
			</Card>
		);
	};
	
	return (
		<PageLayout
			title={StringHelpers.title(lang?.page.title ?? 'Dashboard')}
		>
			<PromoCarousel />
			<div className="dashboard--inner--wrapper d-grid gap-4">
				{lang === null ? (
					<>
						<CardPlaceholder />
						<CardPlaceholder />
						<CardPlaceholder />
						<CardPlaceholder />
						<CardPlaceholder />
						<CardPlaceholder />
					</>
				) : (
					<>
						{renderRegistrationTile()}
						{renderToshibaRewardsTile()}
						{renderVPlusTile()}
						{renderTrainingTile()}
						{renderAdminTile()}
						{renderSupportTile()}
					</>
				)}
			</div>
		</PageLayout>
	);
}

export default Dashboard;
